import React, { useEffect, useRef } from 'react';

const SCRIPT_ID = 'cf-turnstile-script';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

function ensureScriptLoaded() {
  return new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve();
      return;
    }

    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      const startedAt = Date.now();
      const interval = setInterval(() => {
        if (window.turnstile) {
          clearInterval(interval);
          resolve();
          return;
        }
        if (Date.now() - startedAt > 7000) {
          clearInterval(interval);
          reject(new Error('Turnstile script loaded but API was unavailable'));
        }
      }, 100);
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Turnstile'));
    document.head.appendChild(script);
  });
}

export default function TurnstileWidget({ siteKey, onTokenChange, resetSignal }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    let mounted = true;

    if (!siteKey || !containerRef.current) return undefined;

    ensureScriptLoaded()
      .then(() => {
        if (!mounted || !window.turnstile || !containerRef.current) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          appearance: 'always',
          size: 'normal',
          callback: (token) => onTokenChange(token),
          'expired-callback': () => onTokenChange(''),
          'error-callback': () => onTokenChange(''),
        });
      })
      .catch(() => {
        if (mounted) onTokenChange('');
      });

    return () => {
      mounted = false;
      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, onTokenChange]);

  useEffect(() => {
    if (window.turnstile && widgetIdRef.current !== null) {
      window.turnstile.reset(widgetIdRef.current);
      onTokenChange('');
    }
  }, [resetSignal, onTokenChange]);

  if (!siteKey) {
    return <div style={{ color: '#b00020' }}>Captcha is not configured.</div>;
  }

  return <div ref={containerRef} style={{ minHeight: 65 }} />;
}
